import {Injectable} from '@angular/core';
import {ALL_ID, ConceptSelection, FieldFilter, PaginationData, SessionObject} from '@twpub/core/models';
import {NGXLogger} from 'ngx-logger';
import {ClientConfiguration, updateConceptSelection} from '@twpub/core/utils';
import {ConceptService, NavigationService, SessionService} from '@twpub/core/services';
import _ from 'lodash';
import {BehaviorSubject, map, of, take, tap} from 'rxjs';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {ComponentCategory} from '@twpub/core/enums';
import {DEFAULT_PAGE_SIZE, DEFAULT_PGDATA, ResultListProp} from '@twpub/core/constants';

@Injectable({
  providedIn: 'root'
})
export class SharedStateService {
  private sessionSubject: BehaviorSubject<SessionObject>;
  private clientConfig?: ClientConfiguration;
  constructor(private logger: NGXLogger, private sessionService: SessionService,
              private conceptService: ConceptService, private naviService: NavigationService,
              private router: Router, private route: ActivatedRoute) {
    const initialSession = this.sessionService.getSession() || new SessionObject();
    this.sessionSubject = new BehaviorSubject<SessionObject>(initialSession);

    this.fetchClientConfig();

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd && !event.url.startsWith('/admin') && !event.url.startsWith('/superadmin')) {
        this.logger.trace('SharedStateService.navigationEnd')
        this.updateSessionFromUrl(event.url);
      }
    });
  }

  private fetchClientConfig() {
    // Traverse to the deepest child route to get the most specific data
    let currentRoute = this.route.root;
    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
    }
    currentRoute.data.pipe(take(1)).subscribe((data) => {
      this.clientConfig = data.clientConfig;
    });
  }

  updateSessionFromUrl(url: string) {
    const urlTree = this.router.parseUrl(url);
    const paramMap = urlTree.root.children.primary?.segments || [];
    const queryParamMap = urlTree.queryParams;

    let cidIdx = 1;
    let tidIdx = 2;
    let dictId: number | undefined;
    if (paramMap.length === 4) {
      dictId = paramMap[1]?.path ? Number(paramMap[1].path) : undefined;
      cidIdx = 2;
      tidIdx = 3;
    }
    const cid = paramMap[cidIdx]?.path ? Number(paramMap[cidIdx].path) : undefined;
    const tid = paramMap[tidIdx]?.path ? Number(paramMap[tidIdx].path) : undefined;
    const dictParam = queryParamMap.dict;
    const langParam = queryParamMap.lang;
    const termParam = queryParamMap.term;
    const targetParam = queryParamMap.target;
    const sectionParam = queryParamMap.section;
    const domainParam = queryParamMap.domain;

    const updates: Partial<SessionObject> = {};
    if (dictId !== undefined) { // Legacy support for single dictId
      updates.dictIds = [dictId];
    } else {
      updates.dictIds = dictParam && dictParam !== ALL_ID ? dictParam.split(',').map((id: string) => parseInt(id)) : [];
    }
    if (cid !== undefined) updates.conceptId = cid;
    if (tid !== undefined) updates.termId = tid;
    updates.isResultPage = paramMap[0]?.path === 'result';
    if (langParam) updates.sourceLangs = [langParam];
    if (targetParam !== undefined) {
      updates.targetLangs = targetParam && targetParam !== ALL_ID ? targetParam.split(',') : [];
    }
    if (sectionParam) updates.sectionOids = sectionParam.split(',');
    if (domainParam) updates.domainIds = domainParam.split(',').map(Number);
    if (termParam) updates.searchTerm = termParam;
    else if (cid && this.getComponentConfiguration(ComponentCategory.ResultList)?.getBoolean(ResultListProp.NeedSearchForEntry)) {
      updates.searchTerm = cid.toString();
      updates.resultPaginator = {...DEFAULT_PGDATA, pageSize: this.getDefaultPageSize()}; // Reset result index when searching by concept ID
    }

    this.logger.trace('SharedStateService.updateSessionFromUrl:\n' + JSON.stringify(updates));
    this.updateSession(updates);

    if (!langParam && cid) {
      this.findSourceLangFromConcept({conceptId: cid, termId: tid}).pipe(
        tap((sourceLangs) => this.selectSourceLanguages(sourceLangs))
      ).subscribe();
    }
  }

  private findSourceLangFromConcept(selection ?: ConceptSelection) {
    if (!selection?.conceptId) {
      return of([]);
    }
    return this.conceptService.getConcept(selection.conceptId).pipe(
      map((concept) => {
        const term = concept.terms.find(term => term.id === selection.termId);
        return term ? [term.languageCode] : [];
      })
    );
  }

  get session$() {
    return this.sessionSubject.asObservable();
  }

  get sessionObj(): SessionObject {
    return this.sessionSubject.value;
  }

  updateSession(updates: Partial<SessionObject>, publishOnSubject: boolean = true): void {
    const updatedSession = {...this.sessionObj, ...updates};
    this.sessionService.setSession(updatedSession);
    publishOnSubject && this.sessionSubject.next(updatedSession);
    const currentUrl = this.router.url;
    if (!currentUrl.startsWith('/admin') && !currentUrl.startsWith('/superadmin')) {
      this.naviService.updateUrlFromSession(updatedSession);
    }
  }

  private getDefaultPageSize(): number {
    return this.getComponentConfiguration(ComponentCategory.ResultList)
      ?.getNumber(ResultListProp.PageSize, DEFAULT_PAGE_SIZE) ?? DEFAULT_PAGE_SIZE;
  }

  private resetPaginationData = () => ({
    ...this.sessionObj.resultPaginator,
    pageIndex: 0,
    previousPageIndex: 0,
    pageSize: this.getDefaultPageSize(),
    totalItems: -1
  });

  selectDictionaries(dictIds: number[]) {
    if (!_.isEqual(dictIds, this.sessionObj.dictIds)) {
      this.logger.trace('SharedStateService.selectDictionaries:', {dictIds, old: this.sessionObj.dictIds});
      this.logState('Dictionaries', dictIds);
      this.updateSession(this.dictionaryChangeState(dictIds));
    }
  }

  private dictionaryChangeState = (dictIds: number[] | undefined = undefined) => {
    const updatedSession: Partial<SessionObject> = {
      dictIds,
      sectionOids: [], // todo TPUB-175
      domainIds: [],
      conceptId: undefined,
      termId: undefined,
      searchTerm: undefined,
      resultPaginator: this.resetPaginationData(),
      fieldFilters: undefined
    };
    return updatedSession;
  }

  selectSourceLanguages(langCodes: string[]) {
    if (!_.isEqual(langCodes, this.sessionObj.sourceLangs)) {
      this.logState('Source lang', langCodes);
      this.updateSession({sourceLangs: langCodes, resultPaginator: this.resetPaginationData()});
    }
  }

  selectTargetLanguages(langCodes: string[]) {
    if (!_.isEqual(langCodes, this.sessionObj.targetLangs)) {
      this.logState('Target langs', langCodes);
      this.updateSession({targetLangs: langCodes});
    }
  }

  selectConfiguration(configId: number) {
    if (configId !== this.sessionObj.configId) {
      this.logState('Config', configId);
      const updates = {configId, isResultPage: false, ...this.dictionaryChangeState()};
      this.updateSession(updates, false);
      this.reloadPage();
    }
  }

  protected reloadPage() {
    window.location.reload();
  }

  selectSections(oids: string[]) {
    if (!_.isEqual(oids, this.sessionObj.sectionOids)) {
      this.logState('Sections', oids);
      this.updateSession({sectionOids: oids, resultPaginator: this.resetPaginationData()});
    }
  }

  addFieldFilter(fieldFilter: FieldFilter) {
    this.logger.trace('SharedStateService.addFieldFilter:', {fieldFilter});
    const existingFilters = this.sessionObj.fieldFilters || [];
    const updatedFilters = existingFilters.filter((filter) =>
      !_.isEqual(filter.fieldKey, fieldFilter.fieldKey) || fieldFilter.values.length
    );

    if (fieldFilter.values.length) {
      const index = existingFilters.findIndex((filter) =>
        _.isEqual(filter.fieldKey, fieldFilter.fieldKey)
      );

      if (index >= 0) {
        updatedFilters[index] = fieldFilter;
      } else {
        updatedFilters.push(fieldFilter);
      }
    }

    this.logState('Add Field Filter:', updatedFilters);
    this.updateSession({
      fieldFilters: updatedFilters,
      conceptId: undefined,
      termId: undefined,
      resultPaginator: this.resetPaginationData()
    });
  }

  addFieldFilters(fieldFilters: FieldFilter[]) {
    this.logger.trace('SharedStateService.addFieldFilters:', {fieldFilters});
    const existingFilters = this.sessionObj.fieldFilters || [];
    let updatedFilters = [...existingFilters];

    // Process all field filters at once
    fieldFilters.forEach(fieldFilter => {
      // Remove the existing filter for this field key if it exists
      updatedFilters = updatedFilters.filter((filter) =>
        !_.isEqual(filter.fieldKey, fieldFilter.fieldKey)
      );

      // Add the new filter if it has values
      if (fieldFilter.values.length) {
        updatedFilters.push(fieldFilter);
      }
    });

    this.logState('Add Field Filters:', updatedFilters);
    this.updateSession({
      fieldFilters: updatedFilters,
      conceptId: undefined,
      termId: undefined,
      resultPaginator: this.resetPaginationData()
    });
  }

  setPaginationData(pData: PaginationData) {
    if (!_.isEqual(pData, this.sessionObj.resultPaginator)) {
      this.logState('PaginationData', pData);
      this.updateSession({resultPaginator: pData});
    }
  }

  selectConcept(selection: ConceptSelection = {}): void {
    if (updateConceptSelection(this.sessionObj, selection)) {
      this.logState('Concept', selection);
      const updates: Partial<SessionObject> = {...selection};
      // TPUB-417: When result lists with NeedSearchForEntry pass a selection,
      // populate searchTerm with the concept's cid so subsequent searches and
      // URL serialization use the TermWeb-side identifier rather than the
      // internal numeric id. Falls back to conceptId.toString() if cid is
      // unavailable (server didn't include it on the result).
      if (selection.useIdSearch) {
        const idForSearch = selection.cid ?? selection.conceptId?.toString();
        if (idForSearch) {
          updates.searchTerm = idForSearch;
        }
      }
      this.updateSession(updates);
    }
  }

  selectDomains(ids: number[]) {
    if (!_.isEqual(ids, this.sessionObj.domainIds)) {
      this.logState('Domains', ids);
      this.updateSession({domainIds: ids, resultPaginator: this.resetPaginationData()});
    }
  }

  setSearchTerm(searchTerm: string, conceptId: number | undefined = undefined, termId: number | undefined = undefined) {
    this.logState('Search', {searchTerm, conceptId, termId});
    const updatedSession: Partial<SessionObject> = {
      searchTerm,
      conceptId,
      termId,
      resultPaginator: this.resetPaginationData()
    };
    this.updateSession(updatedSession);
  }

  clearConcept() {
    if (this.sessionObj.conceptId || this.sessionObj.termId) {
      const updatedSession: Partial<SessionObject> = {
        conceptId: undefined,
        termId: undefined
      };
      this.logState('Clear concept, was ' + this.sessionObj.conceptId + ':' + this.sessionObj.termId, {});
      this.updateSession(updatedSession);
    }
    return undefined;
  }

  includeAdditionalField(field: string) {
    this.updateSession({additionalField: field});
  }

  setResultPage(isResultPage: boolean): void {
    this.updateSession({isResultPage});
  }

  getSectionOids = (): string[] => this.sessionObj.sectionOids || [];
  getConfigId = (): number | null | undefined => this.sessionObj.configId;
  getDictionaryIds = (): number[] | undefined => this.sessionObj.dictIds;
  getSearchTerm = (): string | undefined => this.sessionObj.searchTerm;
  getSourceLangs = (): string[] => this.sessionObj.sourceLangs || [];
  getTargetLangs = (): string[] => this.sessionObj.targetLangs || [];
  getPaginationData = (): PaginationData => this.sessionObj.resultPaginator || [];
  getResultIndex = (): number => this.sessionObj.resultIndex || 0;
  getFieldFilters = (): FieldFilter[] | undefined => this.sessionObj.fieldFilters
  getAdditionalField = () => this.sessionObj.additionalField || '';
  getDomainIds = () => this.sessionObj.domainIds || [];
  getConceptId = () => this.sessionObj.conceptId;
  getTermId = () => this.sessionObj.termId;
  getComponentConfiguration = (compCategory: ComponentCategory.ResultList) => this.clientConfig?.getConfigurationSafe(compCategory)

  private logState = (prop: string, value: any) => {
    let displayValue;

    if (typeof value === 'number') {
      displayValue = value;
    } else if (value && typeof value === 'object' && Object.keys(value).length > 0) {
      displayValue = JSON.stringify(value);
    } else if (value !== null && value !== undefined && value.length) {
      displayValue = value;
    } else {
      displayValue = '<empty>';
    }

    this.logger.debug(`===State change: ${prop} is ${displayValue}===`);
  };
}
